import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Trophy, Loader2, Star, Sparkles, Award, CalendarClock
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const PRESET_CATEGORIES = [
  'Best Overall', 'Best Character Development', 'Best Hook', 'Best Conceptualization',
  'Most Creative', 'Best Poetry', 'Best Short Story', 'Best Flash Fiction',
  'Most Original Voice', 'Best Imagery', 'Best Dialogue', 'Most Thought-Provoking',
  'Best Ending', "Readers' Choice"
];

export const AdminChallengesTab = ({ allChallenges, genres, api, onDeleteChallenge }) => {
  const navigate = useNavigate();

  // AI Prompt Generator
  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  const [aiPrompt, setAiPrompt] = useState({ genre: '', theme: '', style: '' });
  const [generatedPrompt, setGeneratedPrompt] = useState('');

  // Judge Picker
  const [judgeSearch, setJudgeSearch] = useState('');
  const [judgeResults, setJudgeResults] = useState([]);
  const [selectedJudges, setSelectedJudges] = useState([]);

  // New Challenge Form
  const [submitting, setSubmitting] = useState(false);
  const [newChallenge, setNewChallenge] = useState({
    title: '', description: '', prompt: '', cover_image: '',
    start_date: '', end_date: '', judging_end_date: '',
    award_categories: ['Best Character Development', 'Best Hook', 'Best Conceptualization'],
    genre_restriction: '', word_limit_min: null, word_limit_max: null, judge_ids: []
  });

  // Extend Deadline dialog
  const [scheduleChallenge, setScheduleChallenge] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ end_date: '', judging_end_date: '', reopen: false });
  const [savingSchedule, setSavingSchedule] = useState(false);

  const openScheduleDialog = (challenge) => {
    setScheduleChallenge(challenge);
    setScheduleForm({
      end_date: (challenge.end_date || '').slice(0, 10),
      judging_end_date: (challenge.judging_end_date || '').slice(0, 10),
      reopen: challenge.status === 'completed' || challenge.results_announced
    });
  };

  const handleSaveSchedule = async () => {
    if (!scheduleChallenge) return;
    if (!scheduleForm.end_date && !scheduleForm.judging_end_date && !scheduleForm.reopen) {
      alert('Please change at least one date or toggle Reopen.');
      return;
    }
    setSavingSchedule(true);
    try {
      const payload = {};
      if (scheduleForm.end_date) payload.end_date = scheduleForm.end_date;
      if (scheduleForm.judging_end_date) payload.judging_end_date = scheduleForm.judging_end_date;
      if (scheduleForm.reopen) payload.reopen = true;
      await api.put(`/challenges/${scheduleChallenge.id}/schedule`, payload);
      setScheduleChallenge(null);
      window.location.reload();
    } catch (err) {
      console.error('Extend deadline failed:', err);
      alert(err.response?.data?.detail || 'Failed to update schedule.');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleGeneratePrompt = async () => {
    setGeneratingPrompt(true);
    try {
      const res = await api.post('/ai/generate-prompt', aiPrompt);
      setGeneratedPrompt(res.data.prompt);
    } catch (error) {
      console.error('Failed to generate prompt:', error);
      alert('Failed to generate prompt. Please try again.');
    } finally {
      setGeneratingPrompt(false);
    }
  };

  const handleSearchJudges = async (query) => {
    setJudgeSearch(query);
    if (query.length < 1) { setJudgeResults([]); return; }
    try {
      const res = await api.get(`/users/search?q=${encodeURIComponent(query)}&limit=8`);
      setJudgeResults(res.data.filter(u => !selectedJudges.find(j => j.id === u.id)));
    } catch (e) {
      console.error('Judge search failed:', e);
    }
  };

  const handleCreateChallenge = async (e) => {
    e.preventDefault();
    if (!newChallenge.title.trim() || !newChallenge.prompt.trim()) {
      alert('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/challenges', { ...newChallenge, judge_ids: selectedJudges.map(j => j.id) });
      setSelectedJudges([]);
      setNewChallenge({
        title: '', description: '', prompt: '', cover_image: '',
        start_date: '', end_date: '', judging_end_date: '',
        award_categories: ['Best Character Development', 'Best Hook', 'Best Conceptualization'],
        genre_restriction: '', word_limit_min: null, word_limit_max: null, judge_ids: []
      });
      navigate('/challenges');
    } catch (error) {
      console.error('Failed to create challenge:', error);
      alert('Failed to create challenge');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Existing Challenges List */}
      {allChallenges.length > 0 && (
        <Card className="border-brand-primary/10 mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-brand-accent" />
              Manage Challenges ({allChallenges.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {allChallenges.map((challenge) => (
                <div
                  key={challenge.id}
                  className="flex items-center justify-between p-4 bg-surface-alt rounded-lg"
                  data-testid={`admin-challenge-${challenge.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/challenges/${challenge.id}`}
                        className="font-medium text-text-primary hover:text-brand-accent truncate"
                      >
                        {challenge.title}
                      </Link>
                      {challenge.results_announced && (
                        <Badge className="text-xs bg-green-100 text-green-700">Results Announced</Badge>
                      )}
                    </div>
                    <p className="text-sm text-text-muted mt-1">
                      {challenge.entry_count || 0} entries
                      {challenge.end_date && ` • Ends ${new Date(challenge.end_date).toLocaleDateString()}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openScheduleDialog(challenge)}
                      data-testid={`extend-deadline-${challenge.id}`}
                      title="Change deadline / reopen"
                    >
                      <CalendarClock className="w-4 h-4 mr-1" />
                      Extend
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => onDeleteChallenge(challenge.id, challenge.title)}
                      data-testid={`delete-challenge-${challenge.id}`}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Extend Deadline / Reopen Dialog */}
      <Dialog open={!!scheduleChallenge} onOpenChange={(open) => !open && setScheduleChallenge(null)}>
        <DialogContent className="max-w-md" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle>Extend deadline</DialogTitle>
          </DialogHeader>
          {scheduleChallenge && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-text-muted">{scheduleChallenge.title}</p>
              <div>
                <Label>New submission deadline</Label>
                <Input
                  type="date"
                  value={scheduleForm.end_date}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, end_date: e.target.value })}
                  data-testid="schedule-end-date"
                />
              </div>
              <div>
                <Label>New judging end date</Label>
                <Input
                  type="date"
                  value={scheduleForm.judging_end_date}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, judging_end_date: e.target.value })}
                  data-testid="schedule-judging-date"
                />
              </div>
              {(scheduleChallenge.status === 'completed' || scheduleChallenge.results_announced) && (
                <label className="flex items-start gap-2 text-sm cursor-pointer bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={scheduleForm.reopen}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, reopen: e.target.checked })}
                    data-testid="schedule-reopen"
                  />
                  <span className="text-yellow-900">
                    <strong>Reopen this challenge.</strong> Sets status back to Active and clears the "Results Announced" flag. Existing entries and awards are kept.
                  </span>
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleChallenge(null)} data-testid="schedule-cancel">Cancel</Button>
            <Button onClick={handleSaveSchedule} disabled={savingSchedule} data-testid="schedule-save">
              {savingSchedule ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="border-brand-primary/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-brand-accent" />
            Create New Challenge
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* AI Prompt Generator */}
          <div className="mb-8 p-6 bg-gradient-to-br from-brand-accent/10 to-brand-tertiary/10 rounded-xl">
            <h3 className="font-semibold text-text-primary mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-brand-accent" />
              AI Prompt Generator
            </h3>
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div>
                <Label>Genre (optional)</Label>
                <Select value={aiPrompt.genre || "any"} onValueChange={(v) => setAiPrompt({ ...aiPrompt, genre: v === "any" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Any genre" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any genre</SelectItem>
                    {genres.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Theme (optional)</Label>
                <Input placeholder="e.g., redemption, loss" value={aiPrompt.theme} onChange={(e) => setAiPrompt({ ...aiPrompt, theme: e.target.value })} />
              </div>
              <div>
                <Label>Style (optional)</Label>
                <Input placeholder="e.g., dark, humorous" value={aiPrompt.style} onChange={(e) => setAiPrompt({ ...aiPrompt, style: e.target.value })} />
              </div>
            </div>
            <Button onClick={handleGeneratePrompt} disabled={generatingPrompt} className="btn-accent gap-2" data-testid="generate-prompt-button">
              {generatingPrompt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Generate Prompt
            </Button>
            {generatedPrompt && (
              <div className="mt-4 p-4 bg-white rounded-lg">
                <p className="font-reading text-text-primary whitespace-pre-wrap mb-4">{generatedPrompt}</p>
                <Button onClick={() => { setNewChallenge({ ...newChallenge, prompt: generatedPrompt }); setGeneratedPrompt(''); }} className="btn-primary" data-testid="use-prompt-button">
                  Use This Prompt
                </Button>
              </div>
            )}
          </div>

          {/* Challenge Form */}
          <form onSubmit={handleCreateChallenge} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Challenge Title *</Label>
                <Input placeholder="Enter challenge title" value={newChallenge.title} onChange={(e) => setNewChallenge({ ...newChallenge, title: e.target.value })} required data-testid="challenge-title" />
              </div>
              <div className="space-y-2">
                <Label>Genre Restriction (optional)</Label>
                <Select value={newChallenge.genre_restriction || "any"} onValueChange={(v) => setNewChallenge({ ...newChallenge, genre_restriction: v === "any" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Any genre" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any genre</SelectItem>
                    {genres.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea placeholder="Describe the challenge..." value={newChallenge.description} onChange={(e) => setNewChallenge({ ...newChallenge, description: e.target.value })} rows={2} data-testid="challenge-description" />
            </div>

            <div className="space-y-2">
              <Label>Writing Prompt *</Label>
              <Textarea placeholder="The prompt writers will respond to..." value={newChallenge.prompt} onChange={(e) => setNewChallenge({ ...newChallenge, prompt: e.target.value })} rows={4} required data-testid="challenge-prompt" />
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input type="date" value={newChallenge.start_date} onChange={(e) => setNewChallenge({ ...newChallenge, start_date: e.target.value })} required data-testid="challenge-start-date" />
              </div>
              <div className="space-y-2">
                <Label>Submission Deadline *</Label>
                <Input type="date" value={newChallenge.end_date} onChange={(e) => setNewChallenge({ ...newChallenge, end_date: e.target.value })} required data-testid="challenge-end-date" />
              </div>
              <div className="space-y-2">
                <Label>Judging Ends *</Label>
                <Input type="date" value={newChallenge.judging_end_date} onChange={(e) => setNewChallenge({ ...newChallenge, judging_end_date: e.target.value })} required data-testid="challenge-judging-date" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Cover Image URL (optional)</Label>
              <Input placeholder="https://..." value={newChallenge.cover_image} onChange={(e) => setNewChallenge({ ...newChallenge, cover_image: e.target.value })} />
            </div>

            {/* Judge Picker */}
            <div className="space-y-2" data-testid="judge-picker">
              <Label className="flex items-center gap-2">
                <Star className="w-4 h-4 text-brand-accent" />
                Assign Judges
              </Label>
              <p className="text-xs text-text-muted mb-2">Search for members to assign as judges for this challenge.</p>
              {selectedJudges.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3" data-testid="selected-judges">
                  {selectedJudges.map(j => (
                    <span key={j.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-primary/10 text-brand-primary rounded-full text-sm">
                      {j.display_name || j.username}
                      <button type="button" onClick={() => setSelectedJudges(selectedJudges.filter(x => x.id !== j.id))} className="hover:text-red-500 ml-1">&times;</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="relative">
                <Input placeholder="Search members by name or username..." value={judgeSearch} onChange={(e) => handleSearchJudges(e.target.value)} data-testid="judge-search-input" />
                {judgeResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-[#E5E2DC] rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="judge-search-results">
                    {judgeResults.map(u => (
                      <button key={u.id} type="button" onClick={() => { setSelectedJudges([...selectedJudges, u]); setJudgeSearch(''); setJudgeResults([]); }} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-surface-alt text-left transition-colors">
                        <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                          {(u.display_name || u.username)?.[0]?.toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-text-primary">{u.display_name || u.username}</p>
                          <p className="text-xs text-text-muted">@{u.username}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Award Categories */}
            <div className="space-y-2" data-testid="category-editor">
              <Label className="flex items-center gap-2">
                <Award className="w-4 h-4 text-brand-accent" />
                Award Categories
              </Label>
              <p className="text-xs text-text-muted">Pick from suggestions or add your own.</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {newChallenge.award_categories.map((cat) => (
                  <span key={cat} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                    {cat}
                    <button type="button" onClick={() => setNewChallenge({ ...newChallenge, award_categories: newChallenge.award_categories.filter(c => c !== cat) })} className="ml-1 hover:text-red-500" data-testid={`remove-category-${cat}`}>&times;</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Select onValueChange={(val) => {
                  if (val && !newChallenge.award_categories.includes(val)) {
                    setNewChallenge({ ...newChallenge, award_categories: [...newChallenge.award_categories, val] });
                  }
                }}>
                  <SelectTrigger className="flex-1" data-testid="category-dropdown"><SelectValue placeholder="Add a category..." /></SelectTrigger>
                  <SelectContent>
                    {PRESET_CATEGORIES.filter(c => !newChallenge.award_categories.includes(c)).map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input placeholder="Custom category" className="flex-1" data-testid="custom-category-input" onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const val = e.target.value.trim();
                    if (val && !newChallenge.award_categories.includes(val)) {
                      setNewChallenge({ ...newChallenge, award_categories: [...newChallenge.award_categories, val] });
                      e.target.value = '';
                    }
                  }
                }} />
              </div>
            </div>

            <div className="flex justify-end">
              <Button type="submit" className="btn-accent gap-2" disabled={submitting} data-testid="create-challenge-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
                Create Challenge
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );
};
